import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Table, Popover, Typography, Badge, Tooltip } from 'antd';
import {
  CopyOutlined,
  StarOutlined,
  StarFilled,
  RightOutlined,
  QuestionCircleOutlined,
  PlusSquareOutlined,
  MinusSquareOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { debounce } from 'lodash';
import classnames from 'classnames';
import Copy from '@/components/copy';
import IconTip from '@/components/icon-tip';
import ReceiptListTableMemoized from './../receipt-list-table';
import StarAndManageWrapper from './../star-and-manage-wrapper';
import ActivityCopyWrapper from './../activity-copy-wrapper';
import {
  gotoActivity,
  gotoWaitAreaActivity,
  gotoWaitCheckActivity,
  gotoWaitImprovedActivity,
} from './../../activity-receipt-jump';
import { ActivityReceiptContext } from './../../activity-receipt-context';
import {
  ACTIVITYRECEIPTTYPEENUM,
  STARANDMANAGETYPEENUM,
  ACTIVITYLISTENUM,
  EXTFIELDSENUM,
  ORDERBYENUM,
  computedRatio,
} from './../../activity-receipt-config';
import Dongdong from '@/components/dongdong';
import './index.less';

/****************
 * 活动table【一级为活动table二级为品类table】
 * *****************/
const initPaginations = {
  defaultCurrent: 1,
  current: 1,
  pageSize: 10,
  total: 0,
};

export default function ActivityListTable(props) {
  const [activeTab] = useContext(ActivityReceiptContext);
  const { activityTableList, activityExtFieldsObj, operationCallback, activityTableListLoading } = props;
  const [pagination, setPagination] = useState(initPaginations);
  const [nowSort, setNowSort] = useState({ activityTimeSort: null, lastAssignTimeSort: null });
  // 开始实施手动控制展开的功能
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  useEffect(() => {
    if (activeTab) {
      resetSort(null, null);
      setExpandedRowKeys([]);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activityTableList) {
      setPagination((pre) => ({
        ...pre,
        total: activityTableList?.totalCount || 0,
        current: activityTableList?.page || 1,
      }));
    }
  }, [activityTableList]);

  /* 两种类型的columns */
  const commonColumns = [
    {
      title: '活动信息',
      dataIndex: 'info',
      key: 'info',
      fixed: 'left',
      width: 240,
      render: (value, { baseInfo }) => {
        return (
          <div className="base-info-fa">
            <div className="base-activity-name">
              <span
                title={baseInfo?.name || ''}
                onClick={() => {
                  // 跳转进入活动页面
                  gotoActivity(activeTab, baseInfo);
                }}
              >
                {baseInfo?.name || ''}
              </span>
            </div>
            <div className="base-activity-id-fa">
              活动编号：
              {baseInfo?.id && (
                <Typography.Paragraph
                  style={{ marginBottom: 0, lineHeight: '20px' }}
                  // copyable={{
                  //   text: `活动ID：${baseInfo?.id || ''}，活动名称：${baseInfo?.name || ''}`,
                  //   icon: <CopyOutlined style={{ color: 'rgba(0, 0, 0, 0.65)' }} />,
                  // }}
                >
                  <span style={{ color: 'rgba(0, 0, 0, 0.65)' }}>
                    <Copy text={`活动ID：${baseInfo?.id || ''}，活动名称：${baseInfo?.name || ''}`} size="small">
                      <span className="base-activity-id">
                        <span>{baseInfo?.id || ''}</span>
                      </span>
                    </Copy>
                  </span>
                </Typography.Paragraph>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: '活动时间',
      dataIndex: 'activityTime',
      key: 'activityTime',
      showSorterTooltip: false,
      // defaultSortOrder: 'descend',
      sorter: true,
      sortOrder: nowSort.activityTimeSort,
      render: (value, { baseInfo }) => {
        return (
          <div className="activity-time-fa">
            <div className="activity-time-begin">
              {baseInfo?.beginTime ? moment(baseInfo.beginTime).format('YYYY-MM-DD') : ''}
              {baseInfo?.beginTime && baseInfo?.endTime ? '~' : '-'}
            </div>
            <div className="activity-time-end">
              {baseInfo?.endTime ? moment(baseInfo.endTime).format('YYYY-MM-DD') : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: '报名时间',
      dataIndex: 'activityApplyTime',
      key: 'activityApplyTime',
      render: (value, { baseInfo }) => {
        return (
          <div className="activity-apply-time-fa">
            <div className="activity-apply-time-begin">
              {baseInfo?.applyBeginTime ? moment(baseInfo.applyBeginTime).format('YYYY-MM-DD') : ''}
              {baseInfo?.applyBeginTime && baseInfo?.applyEndTime ? '~' : '-'}
            </div>
            <div className="activity-apply-time-end">
              {baseInfo?.applyEndTime ? moment(baseInfo.applyEndTime).format('YYYY-MM-DD') : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: '待办',
      dataIndex: 'waitdo',
      key: 'waitdo',
      width: '180px',
      render: (value, { baseInfo }) => {
        const baseInfoId = baseInfo?.id || null;
        const activityExtThisValueById = activityExtFieldsObj && baseInfoId ? activityExtFieldsObj[baseInfoId] : null;
        const thisCellShowValueArea =
          baseInfoId && activityExtThisValueById && activityExtThisValueById[EXTFIELDSENUM.USEABLE_AREA_COUNT.key]
            ? activityExtThisValueById[EXTFIELDSENUM.USEABLE_AREA_COUNT.key]
            : null;

        const thisCellShowValueCheck =
          baseInfoId && activityExtThisValueById && activityExtThisValueById[EXTFIELDSENUM.WAIT_CHECK_COUNT.key]
            ? activityExtThisValueById[EXTFIELDSENUM.WAIT_CHECK_COUNT.key]
            : null;

        // 2022.03.31需求新增枚举【待完善商品素材】
        const thisCellShowValueImproved =
          baseInfoId && activityExtThisValueById && activityExtThisValueById[EXTFIELDSENUM.UN_MATERIAL_COUNT.key]
            ? activityExtThisValueById[EXTFIELDSENUM.UN_MATERIAL_COUNT.key]
            : null;

        return (
          <>
            {
              <div className="activity-waitdo-fa">
                {/* 管理的时候没有这个字段 */}
                {activeTab !== ACTIVITYRECEIPTTYPEENUM.MANAGE.key &&
                thisCellShowValueArea &&
                thisCellShowValueArea > 0 ? (
                  <div
                    className="activity-waitdo-area-count activity-waitdo-item-count"
                    onClick={() => {
                      // 跳转“收品池列表页”列表
                      gotoWaitAreaActivity(baseInfo);
                    }}
                  >
                    <Badge size="small" className="badge-area-count" count={thisCellShowValueArea}></Badge>
                    {/* 待我报名收品池 */}
                    <span className="badge-area-count-txt">{EXTFIELDSENUM.USEABLE_AREA_COUNT.des}</span>
                    <RightOutlined />
                  </div>
                ) : null}

                {/* 待完善商品素材:【2022.03.31需求新增枚举】 */}
                {activeTab !== ACTIVITYRECEIPTTYPEENUM.MANAGE.key &&
                thisCellShowValueImproved &&
                thisCellShowValueImproved > 0 ? (
                  <div
                    className="activity-waitdo-material-count activity-waitdo-item-count"
                    onClick={() => {
                      // 跳转“点击可跳转至活动详情页&定位在完善商品素材tab”
                      // http://test1.yx.jd.com/activity-manage/myCreate/daily-activities-operation/materialMaintenance?entry=1&activityId=10629085&from=acore&hideTab=1&id=10629085
                      gotoWaitImprovedActivity(baseInfo);
                    }}
                  >
                    <Badge
                      size="small"
                      className="badge-material-count"
                      style={{ backgroundColor: '#FD8C0E' }}
                      count={thisCellShowValueImproved}
                    ></Badge>
                    {/* 待完善商品素材 */}
                    <span className="badge-material-count-txt">{EXTFIELDSENUM.UN_MATERIAL_COUNT.des}</span>
                    <RightOutlined />
                  </div>
                ) : null}

                {thisCellShowValueCheck && thisCellShowValueCheck > 0 ? (
                  <div
                    className="activity-waitdo-check-count activity-waitdo-item-count"
                    onClick={() => {
                      // 跳转“待审核管理”列表
                      gotoWaitCheckActivity(baseInfo);
                    }}
                  >
                    <Badge
                      size="small"
                      className="badge-check-count"
                      style={{ backgroundColor: '#FD8C0E' }}
                      count={thisCellShowValueCheck}
                    ></Badge>
                    <span className="badge-check-count-txt">{EXTFIELDSENUM.WAIT_CHECK_COUNT.des}</span>
                    <RightOutlined />
                  </div>
                ) : null}

                {/* 三者都没有时显示‘-’ */}
                {activeTab === ACTIVITYRECEIPTTYPEENUM.APPLY.key &&
                  !(thisCellShowValueCheck && thisCellShowValueCheck > 0) &&
                  !(thisCellShowValueArea && thisCellShowValueArea > 0) &&
                  !(thisCellShowValueImproved && thisCellShowValueImproved > 0) &&
                  '-'}
                {activeTab === ACTIVITYRECEIPTTYPEENUM.MANAGE.key &&
                  !(thisCellShowValueCheck && thisCellShowValueCheck > 0) &&
                  '-'}
              </div>
            }
          </>
        );
      },
    },
    {
      title: (
        <>
          <span
            className="title-component-txt"
            // style={{ marginRight: '5px' }}
          >
            完成进度
          </span>
          {/* <Tooltip
            placement="top"
            title={`当前活动所有收品池中，（我已报名资源数+我的下级报名且审核通过的资源数）/我的资源总数`}
          >
            <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
          </Tooltip> */}
          <IconTip
            type="question"
            title={`当前活动所有收品池中，（我已报名资源数+我的下级报名且审核通过的资源数）/我的资源总数`}
          />
        </>
      ),
      dataIndex: 'completeRatio',
      key: 'completeRatio',
      render: (value, { baseInfo }) => {
        const baseInfoId = baseInfo?.id || null;
        const activityExtThisValueById = activityExtFieldsObj && baseInfoId ? activityExtFieldsObj[baseInfoId] : null;
        const thisCellShowValue =
          baseInfoId && activityExtThisValueById && activityExtThisValueById[EXTFIELDSENUM.COMPLETE_RATIO.key]
            ? activityExtThisValueById[EXTFIELDSENUM.COMPLETE_RATIO.key]
            : null;
        return (
          <div
            className={classnames(
              'activity-complete-ratio-fa',
              computedRatio(thisCellShowValue)[0]
                ? computedRatio(thisCellShowValue)[1] === 'OVERDATE'
                  ? 'has-activity-overDate'
                  : 'has-activity-complete'
                : thisCellShowValue
                ? 'no-activity-complete'
                : ''
            )}
          >
            {thisCellShowValue || '-'}
          </div>
        );
      },
    },
    {
      title: '管理组组长',
      dataIndex: 'headMan',
      key: 'headMan',
      render: (value, { baseInfo }) => {
        return (
          <div className="activity-headMan-fa">
            {/* <div className="activity-headMan-name">{baseInfo?.headManName || ''}</div> */}
            <Dongdong erp={baseInfo?.headMan} name={baseInfo?.headManName} />
            {/* <div className="activity-headMan-open-dongdong">
              <img
                style={{ width: '20px', height: '100%', cursor: 'pointer' }}
                src="//img30.360buyimg.com/pop/jfs/t1/145304/26/22937/1574/61f4a6caE1a49f93b/b2805f3e8b56e608.png"
              />
            </div> */}
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 164,
      render: (text, { baseInfo, buttons }) => {
        let collectBtn = {};
        let noIncludeCollectBtns = [];
        if (buttons && Array.isArray(buttons) && buttons.length > 0) {
          collectBtn = buttons.find((item) => item.code === ACTIVITYLISTENUM.YX_ACTIVITY_LIST_COLLECT.key);
          noIncludeCollectBtns = buttons.filter((item) => item.code !== ACTIVITYLISTENUM.YX_ACTIVITY_LIST_COLLECT.key);
        }
        return (
          <div className="buttons-fa">
            <div className="buttons-common-fa">
              {noIncludeCollectBtns.length > 0
                ? noIncludeCollectBtns.map((item) => {
                    if (item.code === ACTIVITYLISTENUM.YX_ACTIVITY_LIST_COPY.key) {
                      return (
                        <UseMemoCopyBtn baseInfo={baseInfo} key={item.code} onClickCopyResult={onClickCopyResult} />
                      );
                    } else if (item.code === ACTIVITYLISTENUM.YX_ACTIVITY_LIST_VIEW.key) {
                      return (
                        <span
                          key={item.code}
                          onClick={() => {
                            // 跳转进入活动页面
                            window.log && window.log('yx_center', 'huichang_click', `{"activityid":"${baseInfo.id}"}`);
                            gotoActivity(activeTab, baseInfo);
                          }}
                          className="btn-item-blue"
                        >
                          {item.name}
                        </span>
                      );
                    } else {
                      return null;
                    }
                  })
                : null}
            </div>
            <div className="buttons-special-fa">
              {collectBtn && <UseMemoCollectBtn baseInfo={baseInfo} collectBtn={collectBtn} />}
            </div>
          </div>
        );
      },
    },
  ];

  /* 报名特有的一列 */
  const manageExtColumns = [
    {
      title: '最新获得资源时间',
      key: 'lastAssignTime',
      dataIndex: 'lastAssignTime',
      showSorterTooltip: false,
      sorter: true,
      sortOrder: nowSort.lastAssignTimeSort,
      render: (value, { baseInfo }) => {
        return (
          <span className="last-assign-time-fa">
            {baseInfo?.lastAssignTime ? moment(baseInfo.lastAssignTime).format('MM-DD HH:mm') : '-'}
          </span>
        );
      },
    },
  ];
  let showClumns = [...commonColumns];
  if (activeTab === ACTIVITYRECEIPTTYPEENUM.APPLY.key) {
    showClumns.splice(3, 0, manageExtColumns[0]);
  }

  // 手动控制展开属性
  function switchExpand(type, record) {
    switch (type) {
      case 'open':
        setExpandedRowKeys((pre) => [...pre, record?.baseInfo?.id]);
        break;
      case 'close':
        setExpandedRowKeys((pre) => pre.filter((item) => item !== record?.baseInfo?.id));
        break;
      default:
        break;
    }
  }

  /*********** 操作回调 ***********/
  function onPaginationChange(page, pageSize) {
    setExpandedRowKeys([]);
    setPagination((pre) => ({ ...pre, current: page }));
    operationCallback('PAGINATIONCHANGE', { page, pageSize });
  }

  function onClickCopyResult() {
    operationCallback('ONCLICKCOPYRESULT');
  }

  function onTableChange(pagination, filters, sorter, { action }) {
    if (action === 'sort') {
      const sorterColumnKey = sorter.columnKey;
      const sorterOrder = sorter.order;
      //互斥、默认排序:活动开始时间倒叙
      switch (sorterColumnKey) {
        case 'activityTime':
          setExpandedRowKeys([]);
          resetSort(sorterOrder, null);
          operationCallback('SORTERORDERCHANGE', {
            currentOrder: sorterOrder === 'ascend' ? ORDERBYENUM.BEGIN_TIME_ASC.key : ORDERBYENUM.BEGIN_TIME_DESC.key,
          });
          break;
        case 'lastAssignTime':
          setExpandedRowKeys([]);
          resetSort(null, sorterOrder);
          operationCallback('SORTERORDERCHANGE', {
            currentOrder:
              sorterOrder === 'ascend'
                ? ORDERBYENUM.LAST_ASSIGN_TIME_ASC.key
                : sorterOrder === 'descend'
                ? ORDERBYENUM.LAST_ASSIGN_TIME_DESC.key
                : ORDERBYENUM.BEGIN_TIME_DESC.key,
          });
          break;
        default:
          break;
      }
    }
  }

  function resetSort(activityTimeSort = null, lastAssignTimeSort = null) {
    setNowSort({ activityTimeSort, lastAssignTimeSort });
  }

  return (
    <div className="activity-list-table-fa">
      <Table
        scroll={{ x: 1300 }}
        loading={activityTableListLoading}
        columns={showClumns}
        rowKey={({ baseInfo }) => baseInfo?.id}
        dataSource={activityTableList?.items}
        onChange={onTableChange}
        rowClassName={() => 'activity-list-row-custom'}
        expandable={{
          expandedRowClassName: (record, index, indent) => {
            return 'activity-list-row-expanded';
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            // 异步控制是否显示可展开图标
            const { baseInfo } = record;
            const baseInfoId = baseInfo?.id || null;
            const activityExtThisValueById =
              activityExtFieldsObj && baseInfoId ? activityExtFieldsObj[baseInfoId] : null;
            const thisCellShowValueArea =
              baseInfoId && activityExtThisValueById && activityExtThisValueById[EXTFIELDSENUM.USEABLE_AREA_COUNT.key]
                ? activityExtThisValueById[EXTFIELDSENUM.USEABLE_AREA_COUNT.key]
                : null;
            const thisCellShowValueCheck =
              baseInfoId && activityExtThisValueById && activityExtThisValueById[EXTFIELDSENUM.WAIT_CHECK_COUNT.key]
                ? activityExtThisValueById[EXTFIELDSENUM.WAIT_CHECK_COUNT.key]
                : null;
            if (
              !(
                (thisCellShowValueArea && thisCellShowValueArea > 0) ||
                (thisCellShowValueCheck && thisCellShowValueCheck > 0)
              )
            ) {
              return false;
            }

            return expanded ? (
              <span className="expand-icon-fa" onClick={(e) => onExpand(record, e)}>
                <img
                  src="//img30.360buyimg.com/pop/jfs/t1/213361/13/19412/443/628dd10fEa23188d4/bd2e9cd955c045a9.png"
                  style={{ width: 20, height: 20 }}
                />
              </span>
            ) : (
              <span className="expand-icon-fa" onClick={(e) => onExpand(record, e)}>
                <img
                  src="//img30.360buyimg.com/pop/jfs/t1/182269/36/23911/539/628dcfdeEbbf70542/a290073010baef6d.png"
                  style={{ width: 20, height: 20 }}
                />
              </span>
            );
          },
          defaultExpandedRowKeys: [],
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            if (!record?.baseInfo?.id) {
              return false;
            }
            if (expanded) {
              debounce(switchExpand, 300)('open', record);
            } else {
              debounce(switchExpand, 300)('close', record);
            }
          },
          expandedRowRender: (record, index, indent, expanded) => {
            return (
              <ReceiptListTableMemoized
                record={record}
                index={index}
                expanded={expanded}
                onCloseReceiptList={() => switchExpand('close', record)}
              />
            );
          },
        }}
        pagination={{
          ...pagination,
          total: pagination.total,
          showSizeChanger: false,
          showTotal: (total) => `总共${total}条`,
          onChange: onPaginationChange,
        }}
      />
    </div>
  );
}

function UseMemoCollectBtn({ baseInfo, collectBtn }) {
  const [newCollectBtn, setNewCollectBtn] = useState(null);
  const [isStarModalVisible, setIsStarModalVisible] = useState(false);
  useEffect(() => {
    if (collectBtn) {
      setNewCollectBtn(collectBtn);
    }
  }, [collectBtn]);

  function onStarSuccess() {
    setNewCollectBtn((pre) => {
      const oldPreCollected = pre?.ext?.collected;
      if (oldPreCollected === 'true') {
        return { ...pre, ext: { ...pre.ext, collected: 'false' } };
      } else if (oldPreCollected === 'false') {
        return { ...pre, ext: { ...pre.ext, collected: 'true' } };
      }
    });
  }

  const memoizedPopoverComponent = useMemo(() => {
    if (newCollectBtn && baseInfo?.id) {
      return (
        <Popover
          content={
            <StarAndManageWrapper
              modalType={STARANDMANAGETYPEENUM.STARTYPE}
              activityId={baseInfo.id}
              isModalVisible={isStarModalVisible}
              setIsModalVisible={setIsStarModalVisible}
              onSuccess={onStarSuccess}
            />
          }
          title={
            <>
              <span style={{ marginRight: '5px' }}>收藏至分组</span>
              <Tooltip placement="top" title={`收藏后，支持在活动报名列表按分组筛选活动。每个用户最多新建10个分组`}>
                <QuestionCircleOutlined style={{ cursor: 'pointer' }} />
              </Tooltip>
            </>
          }
          trigger="click"
          placement="left"
          overlayClassName="star-and-manage-wrapper"
          overlayStyle={{ width: '230px' }}
          destroyTooltipOnHide={true}
          visible={isStarModalVisible}
          onVisibleChange={(bol) => setIsStarModalVisible(bol)}
        >
          {newCollectBtn?.ext?.collected === 'true' ? (
            <Tooltip placement="top" title={`取消或调整分组`}>
              <StarFilled className="fill-star" />
            </Tooltip>
          ) : (
            <Tooltip placement="top" title={`收藏至我的分组`}>
              <StarOutlined className="out-fill-star" />
            </Tooltip>
          )}
        </Popover>
      );
    }
    return null;
  }, [baseInfo.id, isStarModalVisible, newCollectBtn]);

  return <>{memoizedPopoverComponent}</>;
}

function UseMemoCopyBtn({ baseInfo, onClickCopyResult }) {
  const [copyHasSuccess, setCopyHasSuccess] = useState(false);
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);

  function onCopySuccess() {
    setCopyHasSuccess(true);
  }

  const memoizedPopoverComponent = useMemo(() => {
    function onCopyResult() {
      setCopyHasSuccess(false);
      onClickCopyResult();
    }

    if (baseInfo?.id && !copyHasSuccess) {
      return (
        <Popover
          content={
            <ActivityCopyWrapper
              activityId={baseInfo.id}
              setIsCopyModalVisible={setIsCopyModalVisible}
              onCopySuccess={onCopySuccess}
            />
          }
          title="活动复制"
          trigger="click"
          placement="left"
          overlayClassName="activity-copy-wrapper"
          overlayStyle={{ width: '280px' }}
          destroyTooltipOnHide={true}
          visible={isCopyModalVisible}
          onVisibleChange={(bol) => setIsCopyModalVisible(bol)}
        >
          <span className="btn-item-blue">活动复制</span>
        </Popover>
      );
    }
    return (
      <span className="btn-item-blue" onClick={() => onCopyResult()}>
        复制结果
      </span>
    );
  }, [baseInfo.id, isCopyModalVisible, copyHasSuccess, onClickCopyResult]);

  return <>{memoizedPopoverComponent}</>;
}
